import * as React from 'react';
import moment from 'moment';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../../api';
import * as Utils from './utils';
import * as logger from '../../../logger';

import styles from './index.module.css';
import defaultStyles from '../../../styles/formSetStyles.module.css';

import SearchIcon from '@material-ui/icons/Search';

import { Suggestion } from '../../../components/AutocompleteAsyncInput';
import { Button } from '../../../components/Button';
import { ItemsList, Size } from '../../../components/ItemsPage/ItemsList';
import { ItemsPage } from '../../../components/ItemsPage/ItemsPage';
import {
  LoadingSpinner,
  LoadingSpinnerSize,
} from '../../../components/LoadingSpinner';
import ModalBody, { ProcedureToAdd } from './ModalBody';
import { ModalInput } from '../../../components/ModalInput';

const EMPTY_STRING = '';

///////////////////////////////////////////////////
////// TYPES

interface Props extends WithTranslation {
  navigateToNextPage: () => Promise<void>;
  setFormField: (formFieldId: string, value: string) => void;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
}

interface State {
  initialProceduresCount?: number;
  procedures: api.Procedure.PrevisitProcedure[];
  isModalOpen: boolean;
  selectedProcedure: api.Procedure.StandardProcedure | null;
  currentText: string;
}

///////////////////////////////////////////////////
////// COMPONENT

class SurgicalHistoryPageComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const value = props.field.value;
    if (!value) {
      this.state = {
        procedures: [],
        initialProceduresCount: 0,
        isModalOpen: false,
        selectedProcedure: null,
        currentText: EMPTY_STRING,
      };
      return;
    }

    const parsed = Utils.sortProcedures(JSON.parse(value) || []);
    this.state = {
      procedures: parsed,
      initialProceduresCount: parsed.length,
      isModalOpen: false,
      selectedProcedure: null,
      currentText: EMPTY_STRING,
    };
  }

  setIsModalOpen = (newState: boolean) => {
    this.setState({ isModalOpen: newState });
  };

  navigateToNextPage = async (event: React.FormEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    await this.props.navigateToNextPage();
  };

  deleteProcedure = async (id: string) => {
    const { procedures } = this.state;

    if (!procedures.length) return;

    const filteredProcedures = procedures.filter(
      (procedure) => procedure.id !== id,
    );

    this.setState({
      procedures: filteredProcedures,
    });
    await this.updateFormField(filteredProcedures);
  };

  formatProcedures = (procedures: api.Procedure.PrevisitProcedure[]) => {
    const { t } = this.props;
    return procedures.map((procedure) => {
      const {
        name,
        nameRaw,
        laterality,
        dateYear,
        dateMonth,
        dateDay,
        canRemove,
      } = procedure;

      const primaryLabel = t(
        Utils.splitAndUpperFirst(
          name === 'OTHER' ? nameRaw?.toUpperCase() || '' : name,
        ),
      );

      const procedureDate = moment(
        new Date(dateYear || 1, (dateMonth || 1) - 1, dateDay || 1),
      );
      const prettyProcedureDate =
        (dateYear &&
          dateMonth &&
          dateDay &&
          procedureDate.format('MM/DD/YYYY')) ||
        (dateYear && dateMonth && procedureDate.format('MM/YYYY')) ||
        (dateYear && procedureDate.format('YYYY')) ||
        '';
      const secondaryLabel = [
        (laterality && t(Utils.LateralityPrettyMap[laterality])) || '',
        prettyProcedureDate,
      ]
        .filter((x) => !!x)
        .map((elm) => Utils.splitAndUpperFirst(elm || ''))
        .join(', ');

      return {
        ...procedure,
        primaryLabel,
        secondaryLabel,
        canRemove,
      };
    });
  };

  updateFormField = async (procedures: api.Procedure.PrevisitProcedure[]) => {
    const { field, setFormField } = this.props;
    const { id, previsitFormSetId } = field;

    const newValue = JSON.stringify(procedures);
    setFormField(id, newValue);
    try {
      await api.client.Previsit.updateFormField(
        previsitFormSetId,
        id,
        newValue,
      );
    } catch (err) {
      logger.error(err, 'Failed to update form field');
    }
  };

  addProcedure = async (procedureToAdd: ProcedureToAdd) => {
    this.setIsModalOpen(false);
    const { procedures } = this.state;
    const { procedure, nameRaw, dateDay, dateMonth, dateYear, laterality } =
      procedureToAdd;

    if (!Utils.isValidProcedureToAdd(procedureToAdd)) return;

    const newProcedure: api.Procedure.PrevisitProcedure = {
      ...procedure,
      nameRaw: nameRaw || procedure.displayName,
      dateDay: dateDay || null,
      dateMonth: dateMonth || null,
      dateYear: dateYear,
      laterality: laterality,
      canRemove: true,
      comment: null,
    };

    const filteredProcedures = procedures.filter(
      ({ id, name, dateMonth, dateDay, dateYear }) =>
        id !== newProcedure.id ||
        name !== newProcedure.name ||
        dateMonth !== newProcedure.dateMonth ||
        dateDay !== newProcedure.dateDay ||
        dateYear !== newProcedure.dateYear,
    );

    const newProcedures = [...filteredProcedures, newProcedure];

    this.setState({
      procedures: newProcedures,
      selectedProcedure: null,
      currentText: EMPTY_STRING,
    });
    await this.updateFormField(newProcedures);
  };

  handleSelect = (suggestion: Suggestion<api.Procedure.StandardProcedure>) => {
    const { data } = suggestion;

    if (!data) return;

    this.setState({ selectedProcedure: data });
  };

  getProcedureSuggestions = async (
    input: string,
  ): Promise<Suggestion<api.Procedure.StandardProcedure>[]> => {
    const { t } = this.props;
    const cleanedInput = input.trim().toLowerCase();
    if (!cleanedInput) return [];

    const suggestions = Utils.StandardProcedureList.filter(({ displayName }) =>
      displayName.toLowerCase().includes(cleanedInput),
    );

    const standardProcedureOther = Utils.StandardProcedureList.find(
      (suggestion) => suggestion.name === 'OTHER',
    );
    const containsOther = suggestions.find(
      (suggestion) => suggestion.name === 'OTHER',
    );

    const formattedSuggestions = suggestions.map((standardProcedure) => {
      return {
        primaryLabel: t(standardProcedure.displayName),
        data: standardProcedure,
      };
    });

    return [
      ...formattedSuggestions,
      ...(containsOther
        ? []
        : [
            {
              primaryLabel:
                (standardProcedureOther &&
                  t(standardProcedureOther.displayName)) ||
                'Other (specify)',
              data: standardProcedureOther,
            },
          ]),
    ];
  };

  renderModalBody = () => {
    const { selectedProcedure } = this.state;

    return (
      <ModalBody
        selectedProcedure={selectedProcedure}
        addProcedure={this.addProcedure}
        handleSelect={this.handleSelect}
        getProcedureSuggestions={this.getProcedureSuggestions}
        initialText={this.state.currentText}
      />
    );
  };

  render() {
    const { t } = this.props;
    const { procedures, initialProceduresCount, isModalOpen } = this.state;
    if (!procedures) {
      return (
        <div className={styles.LoadingWrapper}>
          <h2>Loading procedures</h2>
          <div className={styles.LoadingSpinnerWrapper}>
            <LoadingSpinner size={LoadingSpinnerSize.Small} />
          </div>
        </div>
      );
    }

    return (
      <div>
        <ItemsPage
          label={
            initialProceduresCount
              ? 'Review and update your surgical history.'
              : 'Add all procedures that apply to your surgical history.'
          }
        >
          <div className={styles.ItemsLists}>
            <ItemsList
              compact={false}
              items={this.formatProcedures(procedures)}
              primaryLabelSize={Size.Small}
              secondaryLabelSize={Size.Small}
              deleteItem={this.deleteProcedure}
              className={styles.ItemsList}
            />
            <div className={styles.ModalInput}>
              <ModalInput
                children={this.renderModalBody()}
                isOpen={isModalOpen}
                setIsOpen={(newState) => this.setIsModalOpen(newState)}
                setCurrentText={(currentText) => this.setState({ currentText })}
                placeholder={t(Utils.SearchPlaceholder)}
                startIcon={<SearchIcon className={styles.SearchIcon} />}
                initialValue={EMPTY_STRING}
                onModalClose={() => {
                  this.setState({
                    isModalOpen: false,
                    selectedProcedure: null,
                    currentText: EMPTY_STRING,
                  });
                }}
              />
            </div>
          </div>
        </ItemsPage>
        <div className={defaultStyles.ButtonSubmitWrapper}>
          <Button
            type="button"
            children={
              procedures && procedures.length
                ? t('Continue')
                : t(`I don't have any surgical history`)
            }
            onClick={this.props.navigateToNextPage}
          />
        </div>
      </div>
    );
  }
}
export default withTranslation()(SurgicalHistoryPageComponent);
